import { useCallback, useMemo, useReducer } from 'react';

import type {
  DestinationOption,
  DestinationScope,
  SaveMode,
} from '@/types/provider-destinations';
import {
  DestinationScope as DestinationScopeEnum,
  RequestTemplateIntent,
  SaveMode as SaveModeEnum,
} from '@/types/provider-destinations';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WizardStep =
  | 'identity_check'
  | 'identity_relink'
  | 'scopes'
  | 'destinations'
  | 'review';

export interface WizardScopeSelection {
  scope: DestinationScope;
  selected: boolean;
  destination: DestinationOption | null;
  createWithoutDestination: boolean;
  saveBehavior: SaveMode;
  followUpPhone: string;
}

export interface CreateRequestWizardState {
  step: WizardStep;
  scopeSelections: WizardScopeSelection[];
  notes: string;
  serviceStartDate: string;
  serviceEndDate: string;
  templateType: RequestTemplateIntent;
  isSubmitting: boolean;
  /** Scopes whose follow-up phone has been auto-populated from the backend. */
  hydratedFollowUpPhoneScopes: DestinationScope[];
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

type WizardAction =
  | { type: 'SET_STEP'; step: WizardStep }
  | { type: 'TOGGLE_SCOPE'; scope: DestinationScope }
  | {
      type: 'SET_DESTINATION';
      scope: DestinationScope;
      destination: DestinationOption | null;
    }
  | {
      type: 'SET_CREATE_WITHOUT_DESTINATION';
      scope: DestinationScope;
      createWithoutDestination: boolean;
    }
  | {
      type: 'SET_SAVE_BEHAVIOR';
      scope: DestinationScope;
      saveBehavior: SaveMode;
    }
  | {
      type: 'SET_SCOPE_FOLLOW_UP_PHONE';
      scope: DestinationScope;
      phone: string;
    }
  | { type: 'SET_NOTES'; notes: string }
  | { type: 'SET_SERVICE_START_DATE'; value: string }
  | { type: 'SET_SERVICE_END_DATE'; value: string }
  | { type: 'SET_TEMPLATE_TYPE'; templateType: RequestTemplateIntent }
  | {
      type: 'INITIALIZE_FROM_TEMPLATE';
      templateType: RequestTemplateIntent;
      selectedScopes: DestinationScope[];
    }
  | { type: 'SET_SUBMITTING'; isSubmitting: boolean }
  | {
      type: 'HYDRATE_SCOPE_FOLLOW_UP_PHONE';
      scope: DestinationScope;
      phone: string;
    }
  | { type: 'RESET' };

// ---------------------------------------------------------------------------
// Initial state factory
// ---------------------------------------------------------------------------

const ALL_SCOPES: DestinationScope[] = [
  DestinationScopeEnum.RECORDS,
  DestinationScopeEnum.BILLS,
  DestinationScopeEnum.RADIOLOGY,
  DestinationScopeEnum.ER_PHYSICIAN,
];

const STANDARD_DEFAULT_SCOPES: DestinationScope[] = [
  DestinationScopeEnum.RECORDS,
  DestinationScopeEnum.BILLS,
];

const createInitialState = (): CreateRequestWizardState => ({
  step: 'scopes',
  scopeSelections: ALL_SCOPES.map((scope) => ({
    scope,
    selected: STANDARD_DEFAULT_SCOPES.includes(scope),
    destination: null,
    createWithoutDestination: false,
    saveBehavior: SaveModeEnum.USE_ONCE,
    followUpPhone: '',
  })),
  notes: '',
  serviceStartDate: '',
  serviceEndDate: '',
  templateType: RequestTemplateIntent.STANDARD,
  isSubmitting: false,
  hydratedFollowUpPhoneScopes: [],
});

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const wizardReducer = (
  state: CreateRequestWizardState,
  action: WizardAction,
): CreateRequestWizardState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step };

    case 'TOGGLE_SCOPE':
      return {
        ...state,
        scopeSelections: state.scopeSelections.map((s) =>
          s.scope === action.scope ? { ...s, selected: !s.selected } : s,
        ),
      };

    case 'SET_DESTINATION':
      return {
        ...state,
        scopeSelections: state.scopeSelections.map((s) =>
          s.scope === action.scope
            ? {
                ...s,
                destination: action.destination,
                createWithoutDestination: false,
              }
            : s,
        ),
      };

    case 'SET_CREATE_WITHOUT_DESTINATION':
      return {
        ...state,
        scopeSelections: state.scopeSelections.map((s) =>
          s.scope === action.scope
            ? {
                ...s,
                createWithoutDestination: action.createWithoutDestination,
                destination: action.createWithoutDestination
                  ? null
                  : s.destination,
              }
            : s,
        ),
      };

    case 'SET_SAVE_BEHAVIOR':
      return {
        ...state,
        scopeSelections: state.scopeSelections.map((s) =>
          s.scope === action.scope
            ? { ...s, saveBehavior: action.saveBehavior }
            : s,
        ),
      };

    case 'SET_SCOPE_FOLLOW_UP_PHONE':
      return {
        ...state,
        scopeSelections: state.scopeSelections.map((s) =>
          s.scope === action.scope ? { ...s, followUpPhone: action.phone } : s,
        ),
        // Mark the scope as hydrated so the auto-populate effect in
        // DestinationSelectionStep does not overwrite the user's manual
        // edit when backend data arrives asynchronously.
        hydratedFollowUpPhoneScopes:
          state.hydratedFollowUpPhoneScopes.includes(action.scope)
            ? state.hydratedFollowUpPhoneScopes
            : [...state.hydratedFollowUpPhoneScopes, action.scope],
      };

    case 'SET_NOTES':
      return { ...state, notes: action.notes };

    case 'SET_SERVICE_START_DATE':
      return { ...state, serviceStartDate: action.value };

    case 'SET_SERVICE_END_DATE':
      return { ...state, serviceEndDate: action.value };

    case 'SET_TEMPLATE_TYPE':
      return { ...state, templateType: action.templateType };

    case 'INITIALIZE_FROM_TEMPLATE':
      return {
        ...state,
        templateType: action.templateType,
        scopeSelections: state.scopeSelections.map((s) => ({
          ...s,
          selected: action.selectedScopes.includes(s.scope),
        })),
      };

    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.isSubmitting };

    case 'HYDRATE_SCOPE_FOLLOW_UP_PHONE':
      return {
        ...state,
        scopeSelections: state.scopeSelections.map((s) =>
          s.scope === action.scope
            ? { ...s, followUpPhone: action.phone }
            : s,
        ),
        hydratedFollowUpPhoneScopes: [
          ...state.hydratedFollowUpPhoneScopes,
          action.scope,
        ],
      };

    case 'RESET':
      return createInitialState();

    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useCreateRequestWizard = () => {
  const [state, dispatch] = useReducer(
    wizardReducer,
    undefined,
    createInitialState,
  );

  const selectedScopes = useMemo(
    () => state.scopeSelections.filter((s) => s.selected),
    [state.scopeSelections],
  );

  const canProceedToDestinations = selectedScopes.length > 0;

  const canProceedToReview = useMemo(
    () =>
      selectedScopes.length > 0 &&
      selectedScopes.every(
        (s) => s.destination !== null || s.createWithoutDestination,
      ),
    [selectedScopes],
  );

  const canSubmit = canProceedToReview && !state.isSubmitting;

  // Action dispatchers
  const setStep = useCallback(
    (step: WizardStep) => dispatch({ type: 'SET_STEP', step }),
    [],
  );

  const toggleScope = useCallback(
    (scope: DestinationScope) => dispatch({ type: 'TOGGLE_SCOPE', scope }),
    [],
  );

  const setDestination = useCallback(
    (scope: DestinationScope, destination: DestinationOption | null) =>
      dispatch({ type: 'SET_DESTINATION', scope, destination }),
    [],
  );

  const setCreateWithoutDestination = useCallback(
    (scope: DestinationScope, createWithoutDestination: boolean) =>
      dispatch({
        type: 'SET_CREATE_WITHOUT_DESTINATION',
        scope,
        createWithoutDestination,
      }),
    [],
  );

  const setSaveBehavior = useCallback(
    (scope: DestinationScope, saveBehavior: SaveMode) =>
      dispatch({ type: 'SET_SAVE_BEHAVIOR', scope, saveBehavior }),
    [],
  );

  const setScopeFollowUpPhone = useCallback(
    (scope: DestinationScope, phone: string) =>
      dispatch({ type: 'SET_SCOPE_FOLLOW_UP_PHONE', scope, phone }),
    [],
  );

  const hydrateScopeFollowUpPhone = useCallback(
    (scope: DestinationScope, phone: string) =>
      dispatch({ type: 'HYDRATE_SCOPE_FOLLOW_UP_PHONE', scope, phone }),
    [],
  );

  const setNotes = useCallback(
    (notes: string) => dispatch({ type: 'SET_NOTES', notes }),
    [],
  );

  const setServiceStartDate = useCallback(
    (value: string) => dispatch({ type: 'SET_SERVICE_START_DATE', value }),
    [],
  );

  const setServiceEndDate = useCallback(
    (value: string) => dispatch({ type: 'SET_SERVICE_END_DATE', value }),
    [],
  );

  const setTemplateType = useCallback(
    (templateType: RequestTemplateIntent) =>
      dispatch({ type: 'SET_TEMPLATE_TYPE', templateType }),
    [],
  );

  const initializeFromTemplate = useCallback(
    (
      templateType: RequestTemplateIntent,
      initialSelectedScopes: DestinationScope[],
    ) =>
      dispatch({
        type: 'INITIALIZE_FROM_TEMPLATE',
        templateType,
        selectedScopes: initialSelectedScopes,
      }),
    [],
  );

  const setSubmitting = useCallback(
    (isSubmitting: boolean) =>
      dispatch({ type: 'SET_SUBMITTING', isSubmitting }),
    [],
  );

  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  return {
    state,
    selectedScopes,
    canProceedToDestinations,
    canProceedToReview,
    canSubmit,
    setStep,
    toggleScope,
    setDestination,
    setCreateWithoutDestination,
    setSaveBehavior,
    setScopeFollowUpPhone,
    hydrateScopeFollowUpPhone,
    setNotes,
    setServiceStartDate,
    setServiceEndDate,
    setTemplateType,
    initializeFromTemplate,
    setSubmitting,
    reset,
  };
};
